import React, { useCallback, useMemo, FC, ComponentProps } from 'react';
import { OptionProps } from 'react-select';

import { useTranslation } from 'Modules/core/i18n';
import { Text, Select2, CustomOption, useMemoDeps, setHtml } from 'Modules/ui';

import { DropdownOption, DropdownProps } from './types';
import styles from './index.module.scss';


const Option: FC<OptionProps<DropdownOption, false>> = ({
    data: { icon, label },
    innerProps,
    innerRef,
    isSelected,
    isFocused,
    isDisabled,
}) => (
    <CustomOption
        ref={innerRef}
        isSelected={isSelected}
        isFocused={isFocused}
        isDisabled={isDisabled}
        {...innerProps}
    >
        <Text<ComponentProps<'div'>>
            component='div'
            ellipsis
            className={styles.option}
            title={label}
            {...icon ? setHtml(`${icon}${label}`) : { children: label }}
        />
    </CustomOption>
);

export const Dropdown: FC<DropdownProps> = React.memo(({
    idx,
    options,
    disabled,
    onClick,
    getValue,
    onChange,
}) => {
    const { t } = useTranslation('filters');

    const dropdownOptions = useMemo(
        () => options.map<DropdownOption>((opt, index) => ({
            label: t(opt.key) as string,
            value: index,
            icon: opt.icon,
        })),
        [options, t],
    );

    const value = useMemoDeps(getValue, [idx]);
    const onDropdownClick = useCallback(
        () => onClick(idx),
        [idx, onClick],
    );
    const onChangeCallback = useMemoDeps(onChange, [idx]);

    return (
        <div className={styles.dropdown} onClick={onDropdownClick}>
            <Select2
                key={idx}
                className={styles.select}
                variant='standalone'
                disabled={disabled}
                options={dropdownOptions}
                value={value}
                onChange={onChangeCallback}
                searchable={false}
                components={{
                    Option,
                }}
            />
        </div>
    );
});
